import {
    createContext,
    useState,
    useEffect,
    useCallback,
    useRef,
    ReactNode,
} from 'react'
import {
    initialiseAnonymousSession as initialiseAnonymousSessionApi,
    logout as logoutApi,
    getSession,
    refreshAccessToken,
} from './api/api'
import type { SessionResponse, TenantInfo } from './api/models'
import { resetAnalytics } from './analytics/analytics'

type SessionUser = NonNullable<SessionResponse['user']>
type NextAction = SessionResponse['nextAction']
type PrincipalType = SessionResponse['principalType']

interface AuthContextType {
    // State
    user: SessionUser | null
    activeTenant: TenantInfo | null
    tenants: TenantInfo[]
    nextAction: NextAction
    principalType: PrincipalType
    anonymousId: string | null
    isAuthenticated: boolean
    isAnonymous: boolean
    loading: boolean
    hasRole: (role: string) => boolean
    hasAnyRole: (roles: string[]) => boolean

    // Actions
    onAuthChanged: () => Promise<void>
    initialiseAnonymousSession: () => Promise<void>
    signOut: () => Promise<void>
    revalidateSession: () => Promise<void>
}

export const AuthContext = createContext<AuthContextType>({
    user: null,
    activeTenant: null,
    tenants: [],
    nextAction: null,
    principalType: 'none',
    anonymousId: null,
    isAuthenticated: false,
    isAnonymous: false,
    loading: true,
    hasRole: () => false,
    hasAnyRole: () => false,
    onAuthChanged: async () => {},
    initialiseAnonymousSession: async () => {},
    signOut: async () => {},
    revalidateSession: async () => {},
})

interface AuthProviderProps {
    children: ReactNode
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
    const [user, setUser] = useState<SessionUser | null>(null)
    const [activeTenant, setActiveTenant] = useState<TenantInfo | null>(null)
    const [tenants, setTenants] = useState<TenantInfo[]>([])
    const [nextAction, setNextAction] = useState<NextAction>(null)
    const [principalType, setPrincipalType] =
        useState<PrincipalType>('none')
    const [anonymousId, setAnonymousId] = useState<string | null>(null)
    const [loading, setLoading] = useState(true)

    const isAuthenticated = principalType === 'user' && user !== null
    const isAnonymous = principalType === 'anonymous'
    const isAnonymousRef = useRef(false)
    const signOutInProgressRef = useRef(false)
    const userInitiatedSignOutRef = useRef(false)
    isAnonymousRef.current = isAnonymous

    const applySession = useCallback((session: SessionResponse) => {
        if (session.principalType === 'user' && session.user) {
            setUser(session.user)
            setActiveTenant(session.activeTenant ?? null)
            setTenants(session.tenants ?? [])
            setNextAction(session.nextAction ?? null)
            setPrincipalType('user')
            setAnonymousId(null)
        } else if (session.principalType === 'anonymous') {
            setUser(null)
            setActiveTenant(session.activeTenant ?? null)
            setTenants(session.tenants ?? [])
            setNextAction(session.nextAction ?? null)
            setPrincipalType('anonymous')
            setAnonymousId(null)
        } else {
            setUser(null)
            setActiveTenant(null)
            setTenants([])
            setNextAction(session.nextAction ?? null)
            setPrincipalType('none')
            setAnonymousId(null)
        }
    }, [])

    const revalidateSession = useCallback(async () => {
        try {
            const session = await getSession()
            applySession(session)
        } catch (error) {
            console.error('Failed to revalidate session:', error)
            // Don't clear state on error — keep existing state so a flaky
            // network call doesn't sign the user out.
        }
    }, [applySession])

    // Public hook for pages to trigger a session re-read after login,
    // register, verify-email, switch-tenant, or join-code redeem.
    const onAuthChanged = revalidateSession

    // Cold-load: ask the backend for the current session. If the access
    // token has expired but the refresh token is still valid, /auth/session
    // returns authenticated=false; we then try a refresh and re-read.
    useEffect(() => {
        const initialiseAuth = async () => {
            try {
                let session = await getSession()
                if (session.principalType === 'none') {
                    const refreshed = await refreshAccessToken()
                    if (refreshed) {
                        session = await getSession()
                    }
                }
                applySession(session)
            } catch (error) {
                console.error('Failed to initialise auth:', error)
                setUser(null)
                setActiveTenant(null)
                setTenants([])
                setNextAction(null)
                setPrincipalType('none')
                setAnonymousId(null)
            } finally {
                setLoading(false)
            }
        }

        initialiseAuth()
    }, [applySession])

    const initialiseAnonymousSession = async () => {
        try {
            const newAnonymousId = await initialiseAnonymousSessionApi()
            if (!newAnonymousId) {
                throw new Error('Failed to create anonymous session')
            }
            setAnonymousId(newAnonymousId)
            setPrincipalType('anonymous')
            await revalidateSession()
        } catch (error) {
            console.error('Error creating anonymous session:', error)
            throw error
        }
    }

    const signOut = useCallback(async () => {
        userInitiatedSignOutRef.current = true
        if (signOutInProgressRef.current) return
        signOutInProgressRef.current = true
        try {
            await logoutApi()
        } catch (error) {
            console.error('Failed to log out via API:', error)
        } finally {
            signOutInProgressRef.current = false
            resetAnalytics()
            setUser(null)
            setActiveTenant(null)
            setTenants([])
            setNextAction(null)
            setPrincipalType('none')
            setAnonymousId(null)
        }
    }, [])

    // Refresh successful → re-read session so role/tenant state stays current.
    useEffect(() => {
        const handleTokenRefreshed = () => {
            if (!userInitiatedSignOutRef.current) {
                void revalidateSession()
            }
        }
        window.addEventListener('auth:token-refreshed', handleTokenRefreshed)
        return () => {
            window.removeEventListener(
                'auth:token-refreshed',
                handleTokenRefreshed
            )
        }
    }, [revalidateSession])

    // Refresh failed → sign out (unless anonymous).
    useEffect(() => {
        const handleRefreshFailed = () => {
            if (!isAnonymousRef.current) {
                void signOut()
            }
        }
        window.addEventListener('auth:refresh-failed', handleRefreshFailed)
        return () => {
            window.removeEventListener(
                'auth:refresh-failed',
                handleRefreshFailed
            )
        }
    }, [signOut])

    const hasRole = useCallback(
        (role: string) => user?.roles?.includes(role) ?? false,
        [user]
    )
    const hasAnyRole = useCallback(
        (roles: string[]) =>
            roles.some((r) => user?.roles?.includes(r) ?? false),
        [user]
    )

    const value: AuthContextType = {
        user,
        activeTenant,
        tenants,
        nextAction,
        principalType,
        anonymousId,
        isAuthenticated,
        isAnonymous,
        loading,
        hasRole,
        hasAnyRole,
        onAuthChanged,
        initialiseAnonymousSession,
        signOut,
        revalidateSession,
    }

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
